/**
 * PermissionsContext
 *
 * Loads the current user's effective capabilities ONCE per org-context change
 * and makes them available everywhere in the tree via `usePermissions()`.
 *
 * This avoids making a `myPermissions` network request on every component that
 * needs to check a capability. Instead components just read from this cache.
 *
 * The cache is invalidated automatically whenever `orgId` changes (org switch).
 *
 * Usage:
 *   const { hasCapability, loading } = usePermissions();
 *   if (hasCapability('org:manage_settings')) { ... }
 */
import { gql, useQuery } from '@apollo/client';
import React, { createContext, useContext, useMemo } from 'react';

import { useUserProfile } from '~/client/hooks/user-profile';

const MY_PERMISSIONS_QUERY = gql`
  query MyPermissionsContext($orgId: ID) {
    myPermissions(orgId: $orgId)
  }
`;

// ── Context shape ─────────────────────────────────────────────────────────────

interface PermissionsContextValue {
  /** Whether the permissions query is still loading */
  loading: boolean;
  /** Full list of capabilities the current user holds in the current org */
  capabilities: string[];
  /** Convenience check — returns true if the user holds the given capability */
  hasCapability: (capability: string) => boolean;
  /** Re-fetch permissions (e.g. after an admin changes the capability matrix) */
  refetch: () => void;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  loading: false,
  capabilities: [],
  hasCapability: () => false,
  refetch: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

interface Props {
  children: React.ReactNode;
}

export function PermissionsProvider({ children }: Props) {
  const {
    userProfile,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useUserProfile();
  const orgId = userProfile.defaultOrganizationId ?? userProfile.orgId;

  const { data, loading, refetch } = useQuery<{ myPermissions: string[] }>(
    MY_PERMISSIONS_QUERY,
    {
      variables: { orgId },
      skip: !isAuthenticated || !orgId,
      fetchPolicy: 'network-only',
      // Re-fetch every 2 minutes so revoked capabilities are reflected quickly
      // (e.g. after an admin removes org:manage_members or platform caps).
      pollInterval: 2 * 60 * 1000,
    }
  );

  const capabilities = data?.myPermissions ?? [];

  const effectiveLoading =
    isAuthLoading || loading || (isAuthenticated && !orgId);

  const value = useMemo<PermissionsContextValue>(
    () => ({
      loading: effectiveLoading,
      capabilities,
      hasCapability: (cap: string) => capabilities.includes(cap),
      refetch,
    }),
    [effectiveLoading, capabilities, refetch]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns the permissions context.
 *
 * @example
 * const { hasCapability } = usePermissions();
 * if (hasCapability('org:manage_settings')) { showAdminPanel(); }
 */
export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}
